import styles from "./CompanyProfilePage.module.scss";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { useEffect, useRef, useState } from "react";
import { useScrollToTop } from "@/frontend/shared/lib/hooks/useScrollToTop";
import { getRouteCompanies, getRoutePortfolioCompanyProfile } from "@/frontend/shared/const/router";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { HeaderSection } from "./HeaderSection/HeaderSection";
import { addSpacesToCamelCase } from "@/frontend/shared/lib/addSpacesToCamelCase/addSpacesToCamelCase";
import { DataRoomSection } from "./DataRoomSection/ui/DataRoomSection";
import { convertStringWithAndSymbol } from "@/frontend/shared/lib/convertStringWithAndSymbol/convertStringWithAndSymbol";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import useHeight from "@/frontend/shared/lib/hooks/useHeight";
import useScrollProgress from "@/frontend/shared/lib/hooks/useScrollProgress";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { AnnotationsSection } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/ui/AnnotationsSection";
import { extractOrganizationId } from "@/frontend/shared/lib/extractOrganizationId/extractOrganizationId";
import { Comments } from "@/frontend/widgets/CommentsSection";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { COMMENTS_BREAKPOINT } from "@/frontend/shared/const/breakpoints";
import { CommentsDrawer } from "@/frontend/widgets/CommentsSection/ui/CommentsDrawer/CommentsDrawer";
import { EActiveView, EDataStatus } from "@/frontend/features/Company/model/types/types";
import { ESectionOptions, Sidebar } from "@/frontend/widgets/Sidebar";
import { FinancingSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/FinancingSection/FinancingSectionUnclaimed";
import { TeamSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/TeamSection/TeamSectionUnclaimed";
import { MarketOverviewSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/MarketOverviewSection/MarketOverviewSectionUnclaimed";
import { SolutionOverviewSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/SolutionOverviewSection/SolutionOverviewSectionUnclaimed";
import { MarketLandscapeSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/MarketLandscapeSection/MarketLandscapeSectionUnclaimed";
import { OverviewSectionUnclaimed } from "@/frontend/features/Company/Profile/ui/OverviewSection/OverviewSectionUnclaimed";
import { getCompanyData } from "../lib/getCompanyData";
import { getCompanyIdFromUrl } from "@/frontend/shared/lib/getCompanyIdFromUrl/getCompanyIdFromUrl";
import { patchCompanySectionNote } from "@/frontend/features/Company/model/patchCompanySectionNote";
import { getFindPortfolioCompany } from "@/frontend/features/Company/model/getFindPortfolioCompany";
import { deleteCompanySectionNote } from "@/frontend/features/Company/model/deleteCompanySectionNote";
import { getCompanyListNotes } from "@/frontend/features/Company/model/getCompanyListNotes";
import { postCompanySectionNote } from "@/frontend/features/Company/model/postCompanySectionNote";

export function CompanyProfilePage() {
  useScrollToTop();

  const companyId = getCompanyIdFromUrl({ url: window.location.href });
  const { navigate } = useHistoricalNavigate();
  const { pathname } = useLocation();
  const organizationId = extractOrganizationId(pathname);

  const [selectedSection, setSelectedSection] = useState<ESectionOptions>(ESectionOptions.OVERVIEW);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialActiveView = searchParams.get("view");
  const [activeView, setActiveView] = useState<EActiveView>((initialActiveView as EActiveView) || EActiveView.PROFILE);

  const { data: userData, isLoading: isLoadingUserData } = useMe();

  const userRole = userData?.entity;
  const isFounder = userRole === ERoleAccess.FOUNDER;
  const isInvestor = userRole === ERoleAccess.INVESTOR;
  const isExpert = userRole === ERoleAccess.EXPERT;

  const { ref: sidebarRef, height: sidebarHeightFromRef } = useHeight();

  const scrollProgress = useScrollProgress();
  const [isContainerScrolled, setIsContainerScrolled] = useState(true);

  useEffect(() => {
    if (scrollProgress > 15 && isContainerScrolled) {
      setIsContainerScrolled(false);
    } else if (scrollProgress < 10 && !isContainerScrolled) {
      setIsContainerScrolled(true);
    }
  }, [scrollProgress, isContainerScrolled]);

  const sectionRefs = {
    [ESectionOptions.OVERVIEW]: useRef(null),
    [ESectionOptions.FINANCING]: useRef(null),
    [ESectionOptions.TEAM]: useRef(null),
    [ESectionOptions.MARKET]: useRef(null),
    [ESectionOptions.PRODUCTS_AND_SERVICES]: useRef(null),
    [ESectionOptions.COMPETITIVE_LANDSCAPE]: useRef(null)
  };

  const {
    detailsData,
    isLoadingDetailsData,
    isFetchingDetailsData,
    refetchDetailsData,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    similarCompaniesDataStatus,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    marketOverviewDataStatus,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    productsDataStatus,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    listNotesData,
    isLoadingListNotesData,
    refetchListNotes
  } = getCompanyData(companyId, isInvestor);

  const notesData = {
    summarySection: listNotesData?.filter((note) => note.section === ESectionOptions.OVERVIEW),
    financingSection: listNotesData?.filter((note) => note.section === ESectionOptions.FINANCING),
    teamSection: listNotesData?.filter((note) => note.section === ESectionOptions.TEAM),
    marketSection: listNotesData?.filter((note) => note.section === ESectionOptions.MARKET),
    solutionOverviewSection: listNotesData?.filter(
      (note) => note.section === convertStringWithAndSymbol(ESectionOptions.PRODUCTS_AND_SERVICES)
    ),
    marketLandscapeSection: listNotesData?.filter(
      (note) => addSpacesToCamelCase(note.section) === ESectionOptions.COMPETITIVE_LANDSCAPE
    )
  };

  const { ref: headerRef, height: headerHeight } = useHeight();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisibleSection = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio + 0.1) {
            // 0.1 is a buffer to prevent rapid switching - A.
            maxRatio = entry.intersectionRatio;
            mostVisibleSection = entry.target.getAttribute("data-section");
          }
        });

        if (mostVisibleSection) {
          setSelectedSection(mostVisibleSection);
        }
      },
      {
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0.01
      }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionRefs, setSelectedSection, headerHeight]);
  const { refetch: refetchPortfolioCompanyId } = useQuery({
    queryKey: ["getFindPortfolioCompany", companyId],
    queryFn: async () => {
      try {
        const response = await getFindPortfolioCompany({ id: companyId });

        if (response.portfolioCompanyId) {
          navigate(getRouteCompanies());
          navigate(getRoutePortfolioCompanyProfile(response.portfolioCompanyId));
        }

        return response;
      } catch {
        return { status: EDataStatus.done, data: [] };
      }
    },
    gcTime: 0,
    refetchOnWindowFocus: false,
    enabled: userRole === ERoleAccess.INVESTOR,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "COMPANY PROFILE: /companies/company/${id}/find-portfolio-company");
      return true;
    }
  });

  const [noteValue, setNoteValue] = useState("");

  async function onClickPost() {
    addSectionNote({ content: noteValue });
    setNoteValue("");
  }

  async function addSectionNote({ content }: { content: string }) {
    await postCompanySectionNote({ content, companyId });
    refetchListNotes();
  }
  const showComments = useScreenWidth() >= COMMENTS_BREAKPOINT;
  const sectionStyles = isInvestor && showComments ? styles.sectionContainer : styles.founderSectionContainer;

  function onChangeActiveView(view: EActiveView) {
    setActiveView(view);
    setSearchParams({ view }, { replace: true });
  }

  const isMobile = useScreenWidth() <= 768;
  const [isCommentsSectionOpen, setIsCommentsSectionOpen] = useState(false);
  const isNotInvestor = userRole === ERoleAccess.FOUNDER || userRole === ERoleAccess.EXPERT;
  const styleMarginTop = isMobile
    ? {}
    : {
        marginTop: `-${sidebarHeightFromRef}px`
      };

  const sectionsContent = (
    <div className={styles.contentBlock} style={styleMarginTop}>
      <div className={styles.subContentContainer}>
        <OverviewSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.OVERVIEW]}
          className={sectionStyles}
          innoveraData={detailsData}
          isLoadingInnoveraData={isLoadingDetailsData}
        />
        <FinancingSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.FINANCING]}
          className={sectionStyles}
          innoveraData={detailsData}
          isLoadingInnoveraData={isLoadingDetailsData}
        />
        <TeamSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.TEAM]}
          className={sectionStyles}
          teamData={teamData}
          isLoadingTeamData={isLoadingTeamData}
        />
        <MarketOverviewSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.MARKET]}
          className={sectionStyles}
          marketOverviewData={marketOverviewData || undefined}
          isLoadingMarketOverviewData={marketOverviewDataStatus != EDataStatus.done}
          marketOverviewDataStatus={marketOverviewDataStatus}
        />
        <SolutionOverviewSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.PRODUCTS_AND_SERVICES]}
          className={sectionStyles}
          patentsData={patentsData}
          isLoadingPatents={isLoadingPatentsData}
          productsData={productsData}
          isLoadingProducts={productsDataStatus != EDataStatus.done}
          productsDataStatus={productsDataStatus}
        />
        <MarketLandscapeSectionUnclaimed
          innerRef={sectionRefs[ESectionOptions.COMPETITIVE_LANDSCAPE]}
          className={sectionStyles}
          isInvestor={isInvestor}
          isLoadingSimilarCompaniesData={similarCompaniesDataStatus != EDataStatus.done}
          refetchSimilarCompaniesData={refetchSimilarCompaniesData}
          similarCompaniesData={similarCompaniesData}
        />
      </div>
    </div>
  );

  const noCommentsContent = (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.headerContent} ref={headerRef}>
            <InnoveraCompanyHeader />
            <HeaderSection
              data={detailsData}
              activeView={activeView}
              onChangeActiveView={setActiveView}
              companyTags={detailsData?.tags}
              companyId={companyId}
              triggerRefetch={refetchPortfolioCompanyId}
              refetchDetailsData={refetchDetailsData}
              isContainerScrolled={isContainerScrolled}
              isFounder={isNotInvestor}
              isExpert={isExpert}
            />
          </div>
          {!isMobile && (
            <div className={styles.commentsSidebarContainer}>
              <Sidebar
                selectedSection={selectedSection}
                sectionRefs={sectionRefs}
                top={headerHeight}
                sidebarRef={sidebarRef}
              />
            </div>
          )}
        </div>
        {sectionsContent}
      </div>
    </div>
  );

  function displayedContent() {
    if (isLoadingUserData) {
      return (
        <div className={styles.container}>
          <InnoveraCompanyHeader />
          <div className={styles.loadingContentContainer}>
            <Loader />
          </div>
        </div>
      );
    }

    if (isInvestor) {
      switch (true) {
        case activeView === EActiveView.DATA_ROOM:
          return (
            <div className={styles.container}>
              <InnoveraCompanyHeader />
              <div className={styles.contentContainer}>
                <HeaderSection
                  data={detailsData}
                  activeView={activeView}
                  onChangeActiveView={onChangeActiveView}
                  companyTags={detailsData?.tags}
                  companyId={companyId}
                  triggerRefetch={refetchPortfolioCompanyId}
                  refetchDetailsData={refetchDetailsData}
                />
                <DataRoomSection
                  id={companyId}
                  triggerRefetch={refetchPortfolioCompanyId}
                  companyName={detailsData?.name || ""}
                  switchSection={setActiveView}
                />
              </div>
            </div>
          );
        case activeView === EActiveView.ANNOTATIONS:
          return (
            <div className={styles.container}>
              <InnoveraCompanyHeader />
              <div className={styles.contentContainer}>
                <HeaderSection
                  data={detailsData}
                  activeView={activeView}
                  onChangeActiveView={onChangeActiveView}
                  companyTags={detailsData?.tags}
                  companyId={companyId}
                  triggerRefetch={refetchPortfolioCompanyId}
                  refetchDetailsData={refetchDetailsData}
                />
                <AnnotationsSection id={companyId} />
              </div>
            </div>
          );
        default:
          return (
            <div className={styles.container}>
              <div className={styles.contentContainer}>
                <div className={styles.headerContainer}>
                  <div className={styles.headerContent} ref={headerRef}>
                    <InnoveraCompanyHeader />
                    <HeaderSection
                      data={detailsData}
                      activeView={activeView}
                      onChangeActiveView={setActiveView}
                      companyTags={detailsData?.tags}
                      companyId={companyId}
                      triggerRefetch={refetchPortfolioCompanyId}
                      refetchDetailsData={refetchDetailsData}
                      isContainerScrolled={isContainerScrolled}
                      setIsCommentsSectionOpen={setIsCommentsSectionOpen}
                      showComments={showComments}
                    />
                  </div>
                  {!isMobile && (
                    <div className={styles.commentsSidebarContainer}>
                      <Sidebar
                        selectedSection={selectedSection}
                        sectionRefs={sectionRefs}
                        top={headerHeight}
                        sidebarRef={sidebarRef}
                      />
                      {showComments && (
                        <div className={styles.commentsContainer}>
                          <Comments
                            data={notesData.summarySection}
                            value={noteValue}
                            setValue={setNoteValue}
                            postNote={onClickPost}
                            triggerRefetch={refetchListNotes}
                            deleteFn={(noteId) => deleteCompanySectionNote({ companyId, noteId })}
                            patchFn={(content, noteId) => patchCompanySectionNote({ content, noteId, companyId })}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {sectionsContent}
              </div>
            </div>
          );
      }
    } else if (isExpert) {
      switch (true) {
        case activeView === EActiveView.ANNOTATIONS:
          return (
            <div className={styles.container}>
              <div className={styles.contentContainer}>
                <div className={styles.headerContainer}>
                  <div className={styles.headerContent} ref={headerRef}>
                    <InnoveraCompanyHeader />
                    <HeaderSection
                      data={detailsData}
                      activeView={activeView}
                      onChangeActiveView={setActiveView}
                      companyTags={detailsData?.tags}
                      companyId={companyId}
                      triggerRefetch={refetchPortfolioCompanyId}
                      refetchDetailsData={refetchDetailsData}
                      isContainerScrolled={isContainerScrolled}
                      isFounder={isNotInvestor}
                      isExpert={userRole === ERoleAccess.EXPERT}
                    />
                  </div>
                </div>
                <AnnotationsSection id={companyId} organizationId={organizationId} />
              </div>
            </div>
          );
        default:
          return noCommentsContent;
      }
    } else if (isFounder) {
      return noCommentsContent;
    }
  }
  return (
    <>
      {displayedContent()}

      <CommentsDrawer
        data={notesData.summarySection}
        value={noteValue}
        setValue={setNoteValue}
        postNote={onClickPost}
        triggerRefetch={refetchListNotes}
        isOpen={isCommentsSectionOpen && !showComments}
        onClose={() => setIsCommentsSectionOpen(false)}
        position="bottom"
        deleteFn={(noteId) => deleteCompanySectionNote({ companyId, noteId })}
        patchFn={(content, noteId) => patchCompanySectionNote({ content, noteId, companyId })}
      />
    </>
  );
}
